import React, { useState, useEffect } from 'react';
import { FileText, Plus, Edit, Trash2, Eye, EyeOff, Upload, X, Save, Users, Target, Eye as EyeIcon, Heart, BarChart3, Phone } from 'lucide-react';
import ApiService from '../../services/api';

interface AboutUs {
  id: number;
  heroTitle: string;
  heroSubtitle?: string;
  heroDescription?: string;
  heroImage?: string;
  storyTitle: string;
  storyContent?: string;
  storyImage?: string;
  missionTitle: string;
  missionContent?: string;
  visionTitle: string;
  visionContent?: string;
  valuesTitle: string;
  valuesContent?: string;
  statsTitle: string;
  statsSubtitle?: string;
  statsData: Array<{ title: string; value: string; icon: string }>;
  ctaTitle: string;
  ctaContent?: string;
  ctaButtonText: string;
  ctaButtonLink: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const AboutUsManagement: React.FC = () => {
  const [aboutUsPages, setAboutUsPages] = useState<AboutUs[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPage, setEditingPage] = useState<AboutUs | null>(null);
  const [homeSettings, setHomeSettings] = useState<any>(null);
  const [formData, setFormData] = useState({
    heroTitle: '',
    heroSubtitle: '',
    heroDescription: '',
    storyTitle: '',
    storyContent: '',
    missionTitle: '',
    missionContent: '',
    visionTitle: '',
    visionContent: '',
    valuesTitle: '',
    valuesContent: '',
    statsTitle: '',
    statsSubtitle: '',
    statsData: [] as Array<{ title: string; value: string; icon: string }>,
    ctaTitle: '',
    ctaContent: '',
    ctaButtonText: '',
    ctaButtonLink: ''
  });
  const [selectedHeroImage, setSelectedHeroImage] = useState<File | null>(null);
  const [selectedStoryImage, setSelectedStoryImage] = useState<File | null>(null);
  const [heroImagePreview, setHeroImagePreview] = useState<string | null>(null);
  const [storyImagePreview, setStoryImagePreview] = useState<string | null>(null);

  const loadAboutUsPages = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAdminAboutUs();
      
             // Ensure statsData is properly parsed as an array for each page
       const aboutUsPages = response.aboutUs || [];
       aboutUsPages.forEach((page: any) => {
        if (page.statsData) {
          if (typeof page.statsData === 'string') {
            try {
              page.statsData = JSON.parse(page.statsData);
            } catch (parseError) {
              console.error('Error parsing statsData:', parseError);
              page.statsData = [];
            }
          } else if (!Array.isArray(page.statsData)) {
            page.statsData = [];
          }
        } else {
          page.statsData = [];
        }
      });
      
      setAboutUsPages(aboutUsPages);
    } catch (error: any) {
      console.error('About us pages load error:', error);
      setError('Hakkımızda sayfaları yüklenemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAboutUsPages();
    const loadHomeSettings = async () => {
      try {
        const response = await ApiService.getHomeSettings();
        setHomeSettings(response);
      } catch (error) {
        console.error('Home settings yüklenirken hata:', error);
      }
    };
    loadHomeSettings();
  }, []);

  const resetForm = () => {
    setFormData({
      heroTitle: '',
      heroSubtitle: '',
      heroDescription: '',
      storyTitle: '',
      storyContent: '',
      missionTitle: '',
      missionContent: '',
      visionTitle: '',
      visionContent: '',
      valuesTitle: '',
      valuesContent: '',
      statsTitle: '',
      statsSubtitle: '',
      statsData: [],
      ctaTitle: '',
      ctaContent: '',
      ctaButtonText: '',
      ctaButtonLink: ''
    });
    setSelectedHeroImage(null);
    setSelectedStoryImage(null);
    setHeroImagePreview(null);
    setStoryImagePreview(null);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'hero' | 'story') => {
    const file = e.target.files?.[0];
    if (file) {
      if (type === 'hero') {
        setSelectedHeroImage(file);
        setHeroImagePreview(URL.createObjectURL(file));
      } else {
        setSelectedStoryImage(file);
        setStoryImagePreview(URL.createObjectURL(file));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const formDataToSend = new FormData();
      
      // Add text fields
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'statsData') {
          formDataToSend.append(key, JSON.stringify(value));
        } else if (typeof value === 'string') {
          formDataToSend.append(key, value);
        }
      });
      
      // Add images
      if (selectedHeroImage) {
        formDataToSend.append('heroImage', selectedHeroImage);
      }
      if (selectedStoryImage) {
        formDataToSend.append('storyImage', selectedStoryImage);
      }

      if (editingPage) {
        await ApiService.updateAboutUs(editingPage.id, formDataToSend);
      } else {
        await ApiService.createAboutUs(formDataToSend);
      }

      setShowModal(false);
      setEditingPage(null);
      resetForm();
      await loadAboutUsPages();
    } catch (error: any) {
      console.error('About us page save error:', error);
      setError('Hakkımızda sayfası kaydedilemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (page: AboutUs) => {
    setEditingPage(page);
    setFormData({
      heroTitle: page.heroTitle,
      heroSubtitle: page.heroSubtitle || '',
      heroDescription: page.heroDescription || '',
      storyTitle: page.storyTitle,
      storyContent: page.storyContent || '',
      missionTitle: page.missionTitle,
      missionContent: page.missionContent || '',
      visionTitle: page.visionTitle,
      visionContent: page.visionContent || '',
      valuesTitle: page.valuesTitle,
      valuesContent: page.valuesContent || '',
      statsTitle: page.statsTitle,
      statsSubtitle: page.statsSubtitle || '',
      statsData: page.statsData || [],
      ctaTitle: page.ctaTitle,
      ctaContent: page.ctaContent || '',
      ctaButtonText: page.ctaButtonText,
      ctaButtonLink: page.ctaButtonLink
    });
    setHeroImagePreview(page.heroImage ? getImageUrl(page.heroImage) : null);
    setStoryImagePreview(page.storyImage ? getImageUrl(page.storyImage) : null);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bu hakkımızda sayfasını silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setLoading(true);
      await ApiService.deleteAboutUs(id);
      await loadAboutUsPages();
    } catch (error: any) {
      console.error('About us page delete error:', error);
      setError('Hakkımızda sayfası silinemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id: number) => {
    try {
      setLoading(true);
      await ApiService.toggleAboutUsActive(id);
      await loadAboutUsPages();
    } catch (error: any) {
      console.error('About us page toggle error:', error);
      setError('Hakkımızda sayfası durumu değiştirilemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imageUrl: string | undefined) => {
    if (!imageUrl) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRkY5M0E2Ii8+CjxwYXRoIGQ9Ik0xMDAgODBDMTA4LjgzNyA4MCAxMTYgODcuMTYzNCAxMTYgOTZDMTE2IDEwNC44MzcgMTA4LjgzNyAxMTIgMTAwIDExMkM5MS4xNjM0IDExMiA4NCAxMDQuODM3IDg0IDk2Qzg0IDg3LjE2MzQgOTEuMTYzNCA4MCAxMDAgODBaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTAwIDEyOEMxMTYuNTY5IDEyOCAxMzAgMTQxLjQzMSAxMzAgMTU4VjE2MEMxMzAgMTY2LjYyNyAxMjQuNjI3IDE3MiAxMTggMTcySDgyQzc1LjM3MjYgMTcyIDcwIDE2Ni42MjcgNzAgMTYwVjE1OEM3MCAxNDEuNDMxIDgzLjQzMTQgMTI4IDEwMCAxMjhaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
    }
    if (imageUrl.startsWith('/uploads/about/')) {
      const filename = imageUrl.replace('/uploads/about/', '');
      return `http://localhost:5000/api/proxy/uploads/about/${filename}`;
    }
    return imageUrl;
  };

  const addStatItem = () => {
    setFormData(prev => ({
      ...prev,
      statsData: [...(Array.isArray(prev.statsData) ? prev.statsData : []), { title: '', value: '', icon: '' }]
    }));
  };

  const updateStatItem = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      statsData: Array.isArray(prev.statsData) ? prev.statsData.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ) : []
    }));
  };

  const removeStatItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      statsData: Array.isArray(prev.statsData) ? prev.statsData.filter((_, i) => i !== index) : []
    }));
  };

  if (loading && aboutUsPages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-white">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Hakkımızda Sayfası Yönetimi</h2>
        <button
          onClick={() => {
            setEditingPage(null);
            resetForm();
            setShowModal(true);
          }}
          className="bg-pink-500 text-white px-4 py-2 rounded-lg hover:bg-pink-600 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Sayfa
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* About Us Pages Table */}
              <div className="rounded-lg shadow-sm overflow-hidden border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
        <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-700">
            <thead style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sayfa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hero Başlık
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Oluşturulma
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
              {aboutUsPages.map((page) => (
                                  <tr key={page.id} className="hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <img
                          className="h-10 w-10 rounded-lg object-cover"
                          src={getImageUrl(page.heroImage)}
                          alt="Hero"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRkY5M0E2Ii8+CjxwYXRoIGQ9Ik0xMDAgODBDMTA4LjgzNyA4MCAxMTYgODcuMTYzNCAxMTYgOTZDMTE2IDEwNC44MzcgMTA4LjgzNyAxMTIgMTAwIDExMkM5MS4xNjM0IDExMiA4NCAxMDQuODM3IDg0IDk2Qzg0IDg3LjE2MzQgOTEuMTYzNCA4MCAxMDAgODBaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTAwIDEyOEMxMTYuNTY5IDEyOCAxMzAgMTQxLjQzMSAxMzAgMTU4VjE2MEMxMzAgMTY2LjYyNyAxMjQuNjI3IDE3MiAxMTggMTcySDgyQzc1LjM3MjYgMTcyIDcwIDE2Ni42MjcgNzAgMTYwVjE1OEM3MCAxNDEuNDMxIDgzLjQzMTQgMTI4IDEwMCAxMjhaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
                          }}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-white">Hakkımızda Sayfası</div>
                        <div className="text-sm text-gray-500">ID: {page.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-white">{page.heroTitle}</div>
                    {page.heroSubtitle && (
                      <div className="text-sm text-gray-500">{page.heroSubtitle}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(page.id)}
                      className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                        page.isActive
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      } transition-colors`}
                    >
                      {page.isActive ? (
                        <><Eye className="w-3 h-3" /> Aktif</>
                      ) : (
                        <><EyeOff className="w-3 h-3" /> Pasif</>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(page.createdAt).toLocaleDateString('tr-TR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(page)}
                      className="text-blue-600 hover:text-blue-900 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(page.id)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="rounded-lg p-4 sm:p-6 w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto text-white" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-white">
                {editingPage ? 'Hakkımızda Sayfası Düzenle' : 'Yeni Hakkımızda Sayfası'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Hero Section */}
              <div className="border-b pb-6">
                <h4 className="text-lg font-medium text-white mb-4">Hero Bölümü</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Hero Başlık *
                    </label>
                    <input
                      type="text"
                      value={formData.heroTitle}
                      onChange={(e) => setFormData({ ...formData, heroTitle: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                      style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Hero Alt Başlık
                    </label>
                    <input
                      type="text"
                      value={formData.heroSubtitle}
                      onChange={(e) => setFormData({ ...formData, heroSubtitle: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                      style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Hero Açıklama
                  </label>
                  <textarea
                    value={formData.heroDescription}
                    onChange={(e) => setFormData({ ...formData, heroDescription: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                  />
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Hero Resmi
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageSelect(e, 'hero')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                  />
                  {heroImagePreview && (
                    <div className="mt-2">
                      <img src={heroImagePreview} alt="Hero preview" className="w-32 h-32 object-cover rounded-lg" />
                    </div>
                  )}
                </div>
              </div>

              {/* Story Section */}
              <div className="border-b pb-6">
                <h4 className="text-lg font-medium text-white mb-4">Hikaye Bölümü</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Hikaye Başlık *
                    </label>
                    <input
                      type="text"
                      value={formData.storyTitle}
                      onChange={(e) => setFormData({ ...formData, storyTitle: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                      style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Hikaye Resmi
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageSelect(e, 'story')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                      style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    />
                    {storyImagePreview && (
                      <div className="mt-2">
                        <img src={storyImagePreview} alt="Story preview" className="w-32 h-32 object-cover rounded-lg" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Hikaye İçeriği
                  </label>
                  <textarea
                    value={formData.storyContent}
                    onChange={(e) => setFormData({ ...formData, storyContent: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                  />
                </div>
              </div>

              {/* Mission & Vision */}
              <div className="border-b pb-6">
                <h4 className="text-lg font-medium text-white mb-4">Misyon & Vizyon</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Misyon Başlık *
                    </label>
                    <input
                      type="text"
                      value={formData.missionTitle}
                      onChange={(e) => setFormData({ ...formData, missionTitle: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                      style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                      required
                    />
                    <textarea
                      value={formData.missionContent}
                      onChange={(e) => setFormData({ ...formData, missionContent: e.target.value })}
                      rows={3}
                      placeholder="Misyon içeriği..."
                      className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                      style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Vizyon Başlık *
                    </label>
                    <input
                      type="text"
                      value={formData.visionTitle}
                      onChange={(e) => setFormData({ ...formData, visionTitle: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                      style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                      required
                    />
                    <textarea
                      value={formData.visionContent}
                      onChange={(e) => setFormData({ ...formData, visionContent: e.target.value })}
                      rows={3}
                      placeholder="Vizyon içeriği..."
                      className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                      style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    />
                  </div>
                </div>
              </div>

              {/* Values */}
              <div className="border-b pb-6">
                <h4 className="text-lg font-medium text-white mb-4">Değerler</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Değerler Başlık *
                  </label>
                  <input
                    type="text"
                    value={formData.valuesTitle}
                    onChange={(e) => setFormData({ ...formData, valuesTitle: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  />
                  <textarea
                    value={formData.valuesContent}
                    onChange={(e) => setFormData({ ...formData, valuesContent: e.target.value })}
                    rows={3}
                    placeholder="Değerler içeriği..."
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="border-b pb-6">
                <h4 className="text-lg font-medium text-white mb-4">İstatistikler</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      İstatistikler Başlık *
                    </label>
                    <input
                      type="text"
                      value={formData.statsTitle}
                      onChange={(e) => setFormData({ ...formData, statsTitle: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                      style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      İstatistikler Alt Başlık
                    </label>
                    <input
                      type="text"
                      value={formData.statsSubtitle}
                      onChange={(e) => setFormData({ ...formData, statsSubtitle: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                      style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    İstatistik Verileri
                  </label>
                  {Array.isArray(formData.statsData) && formData.statsData.map((stat, index) => (
                    <div key={index} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-2">
                      <input
                        type="text"
                        value={stat.title}
                        onChange={(e) => updateStatItem(index, 'title', e.target.value)}
                        placeholder="Başlık"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white text-sm sm:text-base"
                        style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                      />
                      <input
                        type="text"
                        value={stat.value}
                        onChange={(e) => updateStatItem(index, 'value', e.target.value)}
                        placeholder="Değer"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white text-sm sm:text-base"
                        style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                      />
                      <div className="flex gap-2 sm:col-span-2 md:col-span-1">
                        <input
                          type="text"
                          value={stat.icon}
                          onChange={(e) => updateStatItem(index, 'icon', e.target.value)}
                          placeholder="İkon (emoji)"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white text-sm sm:text-base"
                          style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                        />
                        <button
                          type="button"
                          onClick={() => removeStatItem(index)}
                          className="px-2 sm:px-3 py-2 text-red-600 hover:text-red-800 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addStatItem}
                    className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-pink-300 hover:text-pink-500 transition-colors"
                  >
                    + İstatistik Ekle
                  </button>
                </div>
              </div>

              {/* CTA */}
              <div className="border-b pb-6">
                <h4 className="text-lg font-medium text-white mb-4">Çağrı-to-Action</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      CTA Başlık *
                    </label>
                    <input
                      type="text"
                      value={formData.ctaTitle}
                      onChange={(e) => setFormData({ ...formData, ctaTitle: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                      style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      CTA Buton Metni *
                    </label>
                    <input
                      type="text"
                      value={formData.ctaButtonText}
                      onChange={(e) => setFormData({ ...formData, ctaButtonText: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                      style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                      required
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    CTA İçeriği
                  </label>
                  <textarea
                    value={formData.ctaContent}
                    onChange={(e) => setFormData({ ...formData, ctaContent: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                  />
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    CTA Buton Linki *
                  </label>
                  <input
                    type="text"
                    value={formData.ctaButtonLink}
                    onChange={(e) => setFormData({ ...formData, ctaButtonLink: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-300 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Kaydediliyor...' : (editingPage ? 'Güncelle' : 'Oluştur')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutUsManagement;
